import { useCallback, useMemo } from "react";
import { t } from "i18next";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import ResourceDuration from "@components/Booking/ResourceDuration/ResourceDuration";
import { useBooking } from "@hooks/useBooking/useBooking";
import { AvailableTiming, ResourceType } from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";
import { finalTimeSlotsForDrawer } from "@utils/booking.utils";
import { formatPrice } from "@utils/common.utils";

const BookingDrawerStepOne = ({
  currency,
  hasValidResource,
  isBookingDisable,
  priceForResource,
  priceLoader,
  resource,
}: {
  isBookingDisable: boolean;
  priceLoader: boolean;
  hasValidResource: boolean;
  resource?: ResourceType;
  priceForResource?: number;
  currency: string;
}) => {
  const { selectedDate, selectedSlot, selectedTime, timeMaps } = useSelector(
    (State: RootState) => State.booking
  );

  const drawerBookingData = useSelector(
    (State: RootState) => State.drawer.drawerBookingData
  );

  const dispatch = useDispatch();

  const { disableTimeMap } = useBooking();

  const changeSelectedTime = useCallback(
    (time: string) => {
      dispatch(bookingSlice.actions.changeSelectedTime(time));
    },
    [dispatch]
  );

  const timeSlots = useMemo(
    () =>
      finalTimeSlotsForDrawer(
        timeMaps,
        drawerBookingData?.selectedTime,
        selectedDate
      ),
    [
      timeMaps,
      drawerBookingData?.selectedTime,
      selectedDate,
      finalTimeSlotsForDrawer,
    ]
  );

  const selectSlotFromDrawer = (
    slotDetail: AvailableTiming,
    resourceId: string
  ) => {
    const isSameSlot =
      selectedSlot?.resourceId === resourceId &&
      selectedSlot?.slot?.slot === slotDetail.slot;

    if (isSameSlot) {
      return;
    }
    dispatch(
      bookingSlice.actions.changeSelectedSlot({
        isBookable: true,
        resourceId,
        slot: slotDetail,
      })
    );
  };

  return (
    <>
      <div
        className={`fg-time-bubbles-wrap ${disableTimeMap(selectedSlot || {})}`}
        data-testid="drawer-time-select-test"
      >
        <h6 className="mb-3">{moment(selectedDate).format("DD.MM.YY")}</h6>
        <div className="w-100">
          {timeSlots.length > 0 ? (
            <h5 className="mb-3">{t("Drawer.SelectStartTime")}</h5>
          ) : (
            <div className="alert alert-danger small p-2 mt-2">
              {t("noSlotsAvailableForBooking")}
            </div>
          )}
        </div>

        {timeSlots.map(item => (
          <button
            key={item.time}
            className={`btn fg-btn fg-btn-small ${
              item.disabled || isBookingDisable ? "disabled" : ""
            } ${selectedTime === item.time ? "active" : ""}`}
            onClick={() => changeSelectedTime(item.time)}
          >
            {item.time}
          </button>
        ))}
      </div>
      {timeSlots.length > 0 && (
        <div className="fg-units-section">
          <h5 className="mb-3">{t("Drawer.SelectDuration")}</h5>
          {hasValidResource && resource ? (
            <ResourceDuration
              key={selectedSlot?.resourceId}
              resource={resource}
              priceLoader={priceLoader}
              isBookingDisable={isBookingDisable}
              selectSlotFromDrawer={selectSlotFromDrawer}
            />
          ) : (
            <p className="text-danger">{t("noValidResources")}</p>
          )}
        </div>
      )}
      <div className="fg-drawer-time-section total-prize-section mt-2 p-2 bg-default">
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.selectedDate")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>{moment(selectedDate).format("DD.MM.YY")}</h5>
          </div>
        </div>
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.TimeSlot")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>
              {selectedTime || "-"}
              {selectedSlot?.slot?.ends ? " - " + selectedSlot?.slot?.ends : ""}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-7">
            <h5 className="unit-prize-label">{t("Drawer.BasePrice")}</h5>
          </div>
          <div className="col-5 text-end">
            <h5>{formatPrice(priceForResource || 0, currency)}</h5>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingDrawerStepOne;
